import {Component, EventEmitter, inject, Input, OnChanges, Output} from '@angular/core';
import {ObjectContentListSourceArrayService} from '../object-content-list-source-array.service';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {ContentListSourceContainer} from '../../core/definitions/object-content-tab/content-list-source-container';
import {MetaField} from '../../core/definitions/meta-field';
import {SearchResultViewType} from '../../core/definitions/search-result-view-type.enum';
import {SearchTemplateGroupService} from '../../object-search/search-template-group.service';
import {ContentListChoiceService} from '../content-list-choice.service';
import {applyContentListView, isContentListTable, restoreContentListChoice} from '../content-list-view';

@Component({
    selector: 'app-object-content-list-source-array',
    templateUrl: './object-content-list-source-array.component.html',
    styleUrls: ['./object-content-list-source-array.component.scss'],
    standalone: false
})
export class ObjectContentListSourceArrayComponent implements OnChanges {
  // Injections
  private readonly contentListChoice: ContentListChoiceService = inject(ContentListChoiceService);
  private readonly objectContentSourceArrayService: ObjectContentListSourceArrayService = inject(ObjectContentListSourceArrayService);
  private readonly searchTemplateGroupService: SearchTemplateGroupService = inject(SearchTemplateGroupService);

  @Input() contentListSource: ContentListSourceContainer;
  @Output() objectRefresh = new EventEmitter<any>();
  @Output() newObjectCreated = new EventEmitter<string>();

  triggerAnimation = false;
  tableGeneration = 0;

  ngOnChanges() {
    this.restoreSavedChoice();
    this.triggerAnimation = true;
    this.objectContentSourceArrayService.setPageItems(this.contentListSource, true, false).then(() => {
      this.afterPageItems();
      this.triggerAnimation = false;
    });
    if (this.contentListSource.operationContainer) {
      this.contentListSource.refreshCallback = () => {
        this.objectRefresh.emit({store: false});
      };
    }
  }

  isTable(): boolean {
    return isContentListTable(this.contentListSource?.resultViewName);
  }

  selectView(viewName: SearchResultViewType) {
    if (!this.contentListSource || this.contentListSource.resultViewName === viewName) {
      return;
    }
    this.contentListSource.resultViewName = viewName;
    applyContentListView(this.contentListSource.searchContainer, viewName);
    this.contentListChoice.remember(viewName, this.contentListSource.searchContainer?.listFieldTemplateId);
    this.onContentChange(false, false);
  }

  private restoreSavedChoice(): void {
    const saved = this.contentListChoice.saved();
    const source = this.contentListSource;
    if (!source || (!saved.viewName && !saved.templateId)) {
      return;
    }
    const templateName = source.operationContainer?.listFieldTemplateName
      || source.searchContainer?.operationContainer?.listFieldTemplateName;
    const alreadyRestored = (!saved.viewName || source.resultViewName === saved.viewName)
      && source.searchContainer?.listFieldTemplateId === saved.templateId
      && (!saved.templateId || templateName);
    if (alreadyRestored) {
      return;
    }
    restoreContentListChoice(source, saved.viewName, saved.templateId);
    if (!saved.templateId || !source.searchContainer) {
      return;
    }
    const viewName = source.resultViewName;
    this.searchTemplateGroupService.setListFieldTemplateGroup(source.searchContainer, saved.templateId).then(() => {
      if (viewName === SearchResultViewType.LIST || !source.searchContainer) {
        return;
      }
      source.resultViewName = viewName;
      applyContentListView(source.searchContainer, viewName);
    });
  }

  onTableSort(field: MetaField) {
    if (!this.objectContentSourceArrayService.orderByColumn(this.contentListSource, field)) {
      return;
    }
    this.onContentChange(true, false);
  }

  reloadOverview = async (): Promise<void> => {
    await this.objectContentSourceArrayService.setPageItems(this.contentListSource, false, false);
    this.afterPageItems();
  }

  onContentChange(keepRelatedObjects, resetPage, object?: SuperObjectModel) {
    if (object) {
      const changedObjectType = object.object_type;
      if (changedObjectType !== this.contentListSource.parentObject.object_type) {
        this.newObjectCreated.emit(changedObjectType);
        return;
      }
    }
    this.objectContentSourceArrayService.setPageItems(
      this.contentListSource, resetPage, keepRelatedObjects, object).then(() => {
      this.afterPageItems();
    });
  }

  private afterPageItems() {
    if (!this.isTable()) {
      return;
    }
    this.objectContentSourceArrayService.publishTableRows(this.contentListSource);
    this.tableGeneration += 1;
  }
  get disabled() {
    let res = this.contentListSource.operationContainer.art.read_only;
    if (!res) {
      const selections = this.contentListSource.searchContainer.selections;
      res = !(selections.allSelected || !!selections.selectedItems.length) || !this.contentListSource.operationContainer?.operations.length;
    }
    return res;
  }

  onAfterDeleted() {
    this.objectRefresh.emit({store: false});
  }

}
